import { useEffect, useState } from 'react';

import parse from 'html-react-parser';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import { NextSeo } from 'next-seo';
import SEO from 'next-seo.config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { SHOW_COMMEMORATE, SHOW_RIBBON, SHOW_SECTIONCOVER_TOP } from 'src/constants';
import { IMAGE_DEFAULT, WEB_DESC, WEB_KEYWORD, WEB_NAME, WEB_TITLE, WEB_URL } from 'src/constants/layout';
import { decryptionClient } from 'src/services/member-auth/utils/decryption-payload';
import { KEY_ADS_POSITION } from 'src/utils/ads/dynamic-ads';
import { DATALAYER_TYPE } from 'src/utils/datalayer/send-datalayer';
import { convertDatasets } from 'src/utils/datasets';
import { useDesktop } from 'src/utils/device';
import useResponsive from 'src/utils/devices';
import { convertObjPath, convertObjPathNoneEncode, dropSomeField } from 'src/utils/helper';
import { GET } from 'src/utils/services';
import useUser from 'src/utils/useUser';
import styled from 'styled-components';

// Below-the-fold sections stay client-only (ssr:false) to keep INP low.
// Above-the-fold LCP content must be SSR'd so it paints from server HTML (not after hydration).
// Layout must be SSR-enabled for initial page structure
const Layout = dynamic(() => import('src/components/layouts'), { ssr: true });
const SectionLandingPage = dynamic(() => import('src/components/sections/section-landing-page'), { ssr: false });
const SectionSpacial = dynamic(() => import('src/2.0/section-event/section-spacial'), { ssr: false });
const SectionCover = dynamic(() => import('src/components/sections/section-event/section-cover'), { ssr: false });
// SectionHomepageOne is the main highlight = LCP element on a normal visit → SSR it.
const SectionHomepageOne = dynamic(() => import('src/2.0/sections/section-homepage-one'), { ssr: true });
const SectionHomepageTwo = dynamic(() => import('src/2.0/sections/section-homepage-two'), { ssr: false });
const SectionHomepageThree = dynamic(() => import('src/2.0/sections/section-homepage-three'), { ssr: false });
const DynamicAds = dynamic(() => import('src/utils/ads/dynamic-ads'), { ssr: false });
const SectionMemberNews = dynamic(() => import('src/2.0/sections/section-member'), { ssr: false });
const SectionV = dynamic(() => import('src/components/sections/section-v'), { ssr: false });
const WidgetCardHRTLeft = dynamic(() => import('src/2.0/widgets/widget-hrt-left'), { ssr: false });
const FiArrowRightCircle = dynamic(() => import('react-icons/fi').then(mod => mod.FiArrowRightCircle), { ssr: false });

const PageIndex = ({
  ads,
  articles,
  coverData,
  dataLatest,
  articlesTwo,
  dataLandingPage,
  dataAdsHeader,
  dataSetsAll,
  sectionCommemorate,
  dataCommemorate,
  sectionLive,
  sectionElection,
  dataElection,
  sectionWar2026,
  dataWar2026
}) => {
  const { user } = useUser();
  const router = useRouter();
  const [sectionMember, setSectionMember] = useState({});
  const [adsBox, setAdsBox] = useState(true);
  const responsive = useResponsive();
  const _article = {
    title: articles?.metaTag?.title || WEB_TITLE,
    description: articles?.metaTag?.description || WEB_DESC,
    keyword: articles?.metaTag?.keywords || WEB_KEYWORD
  };

  const election = {
    title: convertDatasets(sectionElection, 'title', true) || '',
    link: convertDatasets(sectionElection, 'link', true) || '',
    data: dataElection
  };

  const war2026 = {
    title: convertDatasets(sectionWar2026, 'title', true) || '',
    link: convertDatasets(sectionWar2026, 'link', true) || '',
    data: dataWar2026
  };

  useEffect(() => {
    // console.log('user :', user);
    const checkUser = async () => {
      if (!isEmpty(user)) {
        const response = await fetch('/api/auth/metadata', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        // console.log(response.ok);
        if (response.ok) {
          const res = await response.json();
          localStorage.setItem('login-meta-data', res);
          setSectionMember(decryptionClient(res));
        }
      }
    };

    const loginMetaData = localStorage.getItem('login-meta-data');
    const decryptLoginMetaData = !isEmpty(loginMetaData) ? decryptionClient(loginMetaData) : {};
    const expireCondition = !isEmpty(loginMetaData) ? new Date(decryptLoginMetaData?.expire) < new Date() : true;
    if (expireCondition) {
      // console.log('เข้า expireCondition');
      checkUser();
    } else {
      // console.log('ไม่เข้า expireCondition');
      setSectionMember(decryptLoginMetaData);
    }
  }, [user]);

  const handleLightBoxClick = () => {
    setAdsBox(false);
  };

  const [dataSets, setDataSets] = useState({
    sectionBizelite: [],
    sectionSustainability: []
  });

  const fecthData = async () => {
    if (!isEmpty(dataSetsAll)) {
      // const book = convertDatasets(dataSetsAll, 'bookcase', false);
      setDataSets({
        sectionBizelite: find(dataSetsAll, { topic: 'section-bizelite' }) || [],
        sectionSustainability: find(dataSetsAll, { topic: 'section-sustainability' }) || []
      });
    }
  };

  useEffect(() => {
    fecthData();
  }, [router.isPreview]);

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        {...SEO}
        title={_article?.title}
        description={_article?.description}
        robotsProps={{
          maxImagePreview: 'large',
          maxSnippet: -1,
          maxVideoPreview: -1
        }}
        openGraph={{
          ...SEO.openGraph,
          title: _article?.title,
          description: _article?.description
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _article?.keyword
          }
        ]}
      />
      {/* --------------------------------- HEADER --------------------------------- */}
      <Head>
        <link rel='canonical' href={WEB_URL.WEB} />
        <meta property='twitter:title' content={_article?.title} />
        <meta property='twitter:description' content={_article?.description} />
        <meta property='twitter:image' content={IMAGE_DEFAULT} />
        {/* --------------------------------- Truehit -------------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `
                __th_page="home";
              `
          }}
        />
        {/* ----------------------------- ScriptBrowser ------------------------------------ */}
        {!useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_mobile}`) : ''}
        {useDesktop() && !isEmpty(dataAdsHeader) ? parse(`${dataAdsHeader?.header_desktop}`) : ''}
        {/* ----------------------------- ADS : PPN ------------------------------- */}
        <script async type='text/javascript' src='https://securepubads.g.doubleclick.net/tag/js/gpt.js' />
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: 'var googletag = googletag || {}; window.googletag.cmd = googletag.cmd || [];'
          }}
        />

        {/* ---------------------------- SPIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                          {
                            "@context": "https://schema.org",
                            "@type": "WebSite",
                            "name": "${WEB_NAME}",
                            "url": "${WEB_URL.WEB}",
                            "description": "${_article?.description}",
                            "sameAs": [
                              "${WEB_URL.FACEBOOK}",
                              "${WEB_URL.TWITTER}",
                              "${WEB_URL.INSTAGRAM}",
                              "${WEB_URL.YOUTUBE}",
                              "${WEB_URL.LINE}"
                            ],
                            "potentialAction": {
                              "@type": "SearchAction",
                              "target": "${WEB_URL.WEB}search?q={search_term_string}",
                              "query-input": "required name=search_term_string"
                            }
                          }
                        }
                      `
          }}
        />

        {/* ---------------------------- SPIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "http://schema.org",
                    "@type": "WebPage",
                    "name": "${WEB_NAME}",
                    "description": "${_article?.description}",
                    "publisher": {
                      "@type": "ProfilePage",
                      "name": "${WEB_NAME}"
                    }
                  }
                `
          }}
        />
      </Head>

      {/* ------------------------ First Screen Section ----------------------- */}
      <div>{SectionLandingPage && <SectionLandingPage data={dataLandingPage} />}</div>

      {/* ------------------------ Cover Event on top only ----------------------- */}
      {!isEmpty(coverData) && SHOW_SECTIONCOVER_TOP === 'true' && (
        <div>
          <div className='default-cover'>{SectionCover && <SectionCover data={coverData} />}</div>
        </div>
      )}
      {!isEmpty(sectionCommemorate) && !isEmpty(dataCommemorate) && SHOW_RIBBON === 'true' && (
        <SectionWrapper style={{ filter: SHOW_COMMEMORATE === 'true' ? 'grayscale(0.7)' : 'unset' }}>
          {SectionSpacial && (
            <SectionSpacial id='spacial-commemorate' data={sectionCommemorate}>
              <a className='container spacial-grid'>
                <div className='spacial-grid-list'>
                  {SectionV && (
                    <SectionV
                      spacial={sectionCommemorate}
                      showSpacial={false}
                      showheading={false}
                      count={!responsive.isMobile ? 4 : 3}
                      badge={false}
                      withTarget={true}
                      data={dataCommemorate}
                      heading={convertDatasets(sectionCommemorate, 'ชื่อแท็ก', true)}
                      path={''}
                      //! DATALAYER
                      eventDataLayer={{
                        type: DATALAYER_TYPE.TRACK_POSITION,
                        router: router?.pathname,
                        section: 'section-commemorate',
                        data: {
                          heading: convertDatasets(sectionCommemorate, 'ชื่อแท็ก', true)
                        }
                      }}
                    />
                  )}
                </div>
              </a>
              <a href={`${convertDatasets(sectionCommemorate, 'link', true)}`} className='more'>
                ดูทั้งหมด {FiArrowRightCircle && <FiArrowRightCircle />}
              </a>
            </SectionSpacial>
          )}
        </SectionWrapper>
      )}

      {Layout && (
        <Layout ads={ads}>
          {!isEmpty(coverData) && SHOW_SECTIONCOVER_TOP === 'false' && (
            <div>
              <div className='default-cover'>{SectionCover && <SectionCover data={coverData} />}</div>
            </div>
          )}
          {!isEmpty(ads) && DynamicAds && <DynamicAds position={KEY_ADS_POSITION.LIGHTBOX} data={ads} handler={() => handleLightBoxClick()} adsBox={adsBox} />}
          {!isEmpty(ads) && ads.find(a => a?.position === KEY_ADS_POSITION.BILLBOARD) && (
            <div style={{ minHeight: 300 }}>{DynamicAds && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}</div>
          )}
          {!isEmpty(sectionMember) && SectionMemberNews && (
            <SectionMemberNews
              data={{
                user: dropSomeField(sectionMember, ['menu', 'tags', 'articlesTags', 'articlesCategories'])?.user,
                menu: sectionMember?.menu,
                tags: sectionMember?.tags,
                articlesTags: sectionMember?.articlesTags,
                articlesCategories: sectionMember?.articlesCategories,
                latest: dataLatest?.data
              }}
            />
          )}

          {SectionHomepageOne && (
            <SectionHomepageOne
              id='section-1'
              data={articles}
              lastestNews={dataLatest?.data}
              sectionLive={sectionLive}
              //! DATALAYER
              eventDataLayer={{
                type: DATALAYER_TYPE.TRACK_POSITION,
                router: router?.pathname,
                section: 'section-1',
                position: 'ใต้:โฆษณาตำแหน่งที่ Billboard'
              }}
            />
          )}
          {WidgetCardHRTLeft && (
            <WidgetCardHRTLeft
              id='section-spacial'
              sectionData={sectionWar2026} // ← ส่ง background/header image ผ่าน prop นี้
              article={war2026}
              limit={3} // ← first-card 1 + grid-block 2 items
              eventDataLayer={{
                type: DATALAYER_TYPE.TRACK_POSITION,
                router: router?.pathname,
                section: 'section-spacial',
                position: 'ใต้:section-1'
              }}
              isShowHeader={false}
            />
          )}
          {SectionHomepageTwo && (
            <SectionHomepageTwo
              block1={articles?.block1}
              block2={articles?.block2}
              block3={articles?.block3}
              block4={articlesTwo?.block1}
              // sectionBizelite={dataSets?.sectionBizelite}
              sectionWorldcup={find(dataSetsAll, { topic: 'section-worldcup' })}
              sectionGlobalDialogue={find(dataSetsAll, { topic: 'section-global-dialogue' })}
              sectionNewHorizon={find(dataSetsAll, { topic: 'section-new-horizon' })}
              sectionSustainability={dataSets?.sectionSustainability}
              highlight={articles?.newHighlight2}
              eventDataLayer={{
                type: DATALAYER_TYPE.TRACK_POSITION,
                router: router?.pathname
              }}
            />
          )}
          {SectionHomepageThree && (
            <SectionHomepageThree
              sectionYoutube={dataSets?.sectionYoutube}
              sectionBizelite={dataSets?.sectionBizelite}
              eventDataLayer={{
                type: DATALAYER_TYPE.TRACK_POSITION,
                router: router?.pathname
              }}
            />
          )}
          {!isEmpty(ads) && DynamicAds && <DynamicAds position={KEY_ADS_POSITION.POSTARTICLE} data={ads} />}
          {!isEmpty(ads) && DynamicAds && <DynamicAds position={KEY_ADS_POSITION.AD1X1} data={ads} />}
        </Layout>
      )}
    </>
  );
};

export async function getServerSideProps(ctx) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    articles: null,
    articlesTwo: null,
    coverData: [],
    dataSetsAll: null,
    dataLandingPage: [],
    ads: [],
    sectionLive: [],
    sectionElection: [],
    sectionWar2026: [],
    dataWar2026: [],
    sectionEvent: [],
    dataElection: [],
    sectionCommemorate: [],
    dataCommemorate: [],
    dataAdsHeader: null
  };
  // Homepage SSR output is identical for all visitors (user data is fetched client-side),
  // so it is safe to cache at the CDN. This removes the TTFB cost for the vast majority of hits.
  ctx.res.setHeader('Cache-Control', 'public, s-maxage=60, stale-while-revalidate=300');
  try {
    const payload = {
      block1: 8,
      block2: 8,
      block3: 8,
      headerLatestNews: 5
    };

    function getTimestamp() {
      return Math.floor(Date.now() / 1000);
    }
    const timestampInSeconds = getTimestamp();

    // Batch 1: all independent requests fire together instead of sequentially (cuts TTFB).
    const [sectionOne, sectionTwo, scriptBrowser, cover, landinPage, latest, dataSetSectionEvent, dataSetsAll] = await Promise.all([
      GET(`/api/v1.0/home/section-one${convertObjPath(payload)}`, false, true),
      GET(`/api/v1.0/home/section-two${convertObjPath(payload)}`, false, true),
      GET('/api/headerAds'),
      GET('/api/v1.0/imagecover'),
      GET('/api/landing-page/list'),
      GET('/api/v1.0/categories/latest-news-by-one', false, true),
      GET('/api/v1.0/categories/event/upcoming/advertorials', false, true),
      GET(`/api/datasets?${timestampInSeconds}&limit=50`)
    ]);

    if (!isEmpty(scriptBrowser)) {
      initProps.dataAdsHeader = scriptBrowser || '';
    }
    if (!isEmpty(sectionOne)) {
      initProps.ads = sectionOne?.ads || [];
      initProps.articles = sectionOne || [];
    }
    if (!isEmpty(sectionTwo)) {
      initProps.articlesTwo = sectionTwo || [];
    }
    if (!isEmpty(cover)) initProps.coverData = cover;
    if (!isEmpty(landinPage)) initProps.dataLandingPage = landinPage;
    if (!isEmpty(latest)) initProps.dataLatest = latest;
    if (!isEmpty(dataSetSectionEvent)) {
      initProps.sectionEvent = dataSetSectionEvent.data || [];
    }

    if (!isEmpty(dataSetsAll)) {
      const controlAdsBillborad = find(dataSetsAll, { topic: 'control-ads-billborad' })?.dataSet || [];
      const linkUSElection = find(dataSetsAll, { topic: 'us-election' }) || [];
      initProps.usElectionLink = linkUSElection;
      initProps.positionsAdsBillborad = convertDatasets(controlAdsBillborad, 'positions', true) || '';
      initProps.dataSetsAll = dataSetsAll;

      const sectionSeven = find(dataSetsAll, { topic: 'section-7-right' })?.dataSet || [];
      const dataSetLive = find(dataSetsAll, { topic: 'section-live' })?.dataSet || [];
      const dataSetElection = find(dataSetsAll, { topic: 'section-election' })?.dataSet || [];
      const dataSetWar2026 = find(dataSetsAll, { topic: 'section-war2026' })?.dataSet || [];
      const dataSetCommemorate = find(dataSetsAll, { topic: 'section-commemorate' })?.dataSet || [];

      if (!isEmpty(dataSetLive)) initProps.sectionLive = dataSetLive || [];
      if (!isEmpty(dataSetElection)) initProps.sectionElection = dataSetElection || [];
      if (!isEmpty(dataSetWar2026)) initProps.sectionWar2026 = dataSetWar2026 || [];
      if (!isEmpty(dataSetCommemorate)) initProps.sectionCommemorate = dataSetCommemorate || [];

      const sectionSevenCategory = !isEmpty(sectionSeven) ? convertDatasets(sectionSeven, 'category', true) : '';
      const electionTag = !isEmpty(dataSetElection) ? convertDatasets(dataSetElection, 'ชื่อแท็ก', true) || [] : null;
      const war2026Tag = !isEmpty(dataSetWar2026) ? convertDatasets(dataSetWar2026, 'ชื่อแท็ก', true) || [] : null;
      const commemorateTag = !isEmpty(dataSetCommemorate) ? convertDatasets(dataSetCommemorate, 'ชื่อแท็ก', true) || [] : null;

      // Batch 2: requests derived from dataSetsAll are independent of each other → run in parallel.
      const [resCate, electionData, war2026Data, commemorateData] = await Promise.all([
        sectionSevenCategory ? GET(`/api/v1.0/categories${sectionSevenCategory}`, false, true) : null,
        electionTag ? GET(`/api/search/tag${convertObjPathNoneEncode({ name: encodeURIComponent(electionTag) })}`) : null,
        war2026Tag ? GET(`/api/search/tag${convertObjPathNoneEncode({ name: encodeURIComponent(war2026Tag) })}`) : null,
        commemorateTag ? GET(`/api/search/tag${convertObjPathNoneEncode({ name: encodeURIComponent(commemorateTag) })}`) : null
      ]);

      if (!isEmpty(resCate)) {
        initProps.sectionSevenCate = resCate;
        initProps.sectionSevenCateName = sectionSevenCategory || '';
      }
      if (!isEmpty(electionData)) initProps.dataElection = electionData || [];
      if (!isEmpty(war2026Data)) initProps.dataWar2026 = war2026Data || [];
      if (!isEmpty(commemorateData)) initProps.dataCommemorate = commemorateData || [];
    }
  } catch (err) {
    console.log('==========> INDEX ERROR', err.message);
    initProps.errMessage = err.message;
    initProps.statusCode = err?.response?.status || 500;
  }
  return {
    props: initProps
  };
}

export default PageIndex;

const SectionWrapper = styled.div`
  #spacial-commemorate {
    position: relative;
    margin-top: 0px;
    .spacial-grid {
      padding: 16rem 1rem 2rem !important;
      @media (max-width: 1024px) {
        padding: 9rem 0.75rem 0rem !important;
      }
    }
    .card-v-content-meta {
      margin-top: 5px;
    }
    .card-v-thumb {
      margin-bottom: 10px;
    }
    .more {
      color: #fff;
      position: absolute;
      bottom: 11px;
      right: 30px;
      font-size: 16pt;
      :hover {
        color: #ffffff92;
      }
    }
    @media (max-width: 767px) {
      .card-v {
        min-height: 133px;
      }
      .card-v-content-title {
        font-size: 16pt;
        min-height: 56px;
      }
      .card-v-content-meta {
        font-size: 10pt;
      }
    }
  }
  #spacial-commemorate {
    background-position: top;
    @media (min-width: 767px) and (max-width: 1024px) {
      background-size: contain;
      background-position: top center;
      background-color: black;
    }

    .spacial-grid {
      padding: 20rem 1rem 2rem;
      @media (max-width: 1024px) {
        padding: 11rem 1rem 0rem;
      }
    }
    .spacial-card {
      border-radius: 8px;
    }
    .spacial-grid-list .row {
      grid-gap: 20px;
      grid-template-columns: 1fr 1fr 1fr 1fr;
      @media (max-width: 1024px) {
        grid-template-columns: 1fr 1fr 1fr 1fr;
        grid-template-areas: '--item-1 --item-2 --item-3 --item-4 ';
        .card-v {
          min-height: 140px;
        }
      }
      @media (max-width: 690px) {
        grid-template-columns: 1fr 1fr 1fr;
        grid-gap: 5px;
        grid-template-areas: '--item-1 --item-2 --item-3';
        .card-v {
          min-height: 100px;
        }
      }
    }
  }
`;
